(() => {
  // utils/components/cart-performance.ts
  class CartPerformance {
    static #metric_prefix = "cart-performance";
    static createStartingMarker(benchmarkName) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      return performance.mark(`${metricName}:start`);
    }
    static measureFromEvent(benchmarkName, event) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const startMarker = performance.mark(`${metricName}:start`, {
        startTime: event.timeStamp,
      });
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(
        metricName,
        `${metricName}:start`,
        `${metricName}:end`,
      );
    }
    static measureFromMarker(benchmarkName, startMarker) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(metricName, startMarker.name, `${metricName}:end`);
    }
    static measure(benchmarkName, callback) {
      const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
      const startMarker = performance.mark(`${metricName}:start`);
      callback();
      const endMarker = performance.mark(`${metricName}:end`);
      performance.measure(
        metricName,
        `${metricName}:start`,
        `${metricName}:end`,
      );
    }
  }
  // utils/components/deferred-media.ts
  class DeferredMedia extends HTMLElement {
    constructor() {
      super();
      const poster = this.querySelector('[id^="Deferred-Poster-"]');
      if (!poster) return;
      poster.addEventListener("click", () => this.loadContent());
    }
    loadContent(focus = true) {
      window.pauseAllMedia();
      if (!this.getAttribute("loaded")) {
        const content = document.createElement("div");
        const template = this.querySelector("template");
        const firstElement = template?.content.firstElementChild;
        if (firstElement) {
          content.appendChild(firstElement.cloneNode(true));
        }
        this.setAttribute("loaded", "true");
        const mediaElement = content.querySelector(
          "video, model-viewer, iframe",
        );
        if (!mediaElement) return;
        const deferredElement = this.appendChild(mediaElement);
        if (focus && typeof deferredElement.focus === "function") {
          deferredElement.focus();
        }
        if (
          deferredElement.nodeName === "VIDEO" &&
          deferredElement.getAttribute("autoplay")
        ) {
          deferredElement.play?.();
        }
        const formerStyle = deferredElement.getAttribute("style");
        deferredElement.setAttribute("style", "display: block;");
        window.setTimeout(() => {
          deferredElement.setAttribute("style", formerStyle ?? "");
        }, 0);
      }
    }
  }
  // utils/getFocusableElements.ts
  var getFocusableElements = (container) => {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
      ),
    );
  };

  // utils/trapFocus.ts
  var trapFocusHandlers = {};
  var trapFocus = (container, elementToFocus = container) => {
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];
    removeTrapFocus();
    trapFocusHandlers.focusin = (event) => {
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
        return;
      if (trapFocusHandlers.keydown) {
        document.addEventListener("keydown", trapFocusHandlers.keydown);
      }
    };
    trapFocusHandlers.focusout = () => {
      if (trapFocusHandlers.keydown) {
        document.removeEventListener("keydown", trapFocusHandlers.keydown);
      }
    };
    trapFocusHandlers.keydown = (event) => {
      if (event.code.toUpperCase() !== "TAB") return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };
    document.addEventListener("focusout", trapFocusHandlers.focusout);
    document.addEventListener("focusin", trapFocusHandlers.focusin);
    elementToFocus.focus();
    if (elementToFocus instanceof HTMLInputElement) {
      if (
        ["search", "text", "email", "url"].includes(elementToFocus.type) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    }
  };
  var removeTrapFocus = (elementToFocus = null) => {
    if (trapFocusHandlers.focusin) {
      document.removeEventListener("focusin", trapFocusHandlers.focusin);
    }
    if (trapFocusHandlers.focusout) {
      document.removeEventListener("focusout", trapFocusHandlers.focusout);
    }
    if (trapFocusHandlers.keydown) {
      document.removeEventListener("keydown", trapFocusHandlers.keydown);
    }
    if (elementToFocus) elementToFocus.focus();
  };
  // utils/components/modal-dialog.ts
  class ModalDialog extends HTMLElement {
    openedBy;
    moved;
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]')?.addEventListener(
        "click",
        this.hide.bind(this),
      );
      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") this.hide();
      });
      if (this.classList.contains("media-modal")) {
        this.addEventListener("pointerup", (event) => {
          if (
            event.pointerType === "mouse" &&
            !event.target.closest("deferred-media, product-model")
          )
            this.hide();
        });
      } else {
        this.addEventListener("click", (event) => {
          if (event.target === this) this.hide();
        });
      }
    }
    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      const section = this.closest(".shopify-section");
      if (section) {
        this.dataset.section = section.id.replace("shopify-section-", "");
      }
      document.body.appendChild(this);
    }
    show(opener) {
      this.openedBy = opener;
      const popup = this.querySelector(".template-popup");
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      if (popup) popup.loadContent();
      const dialog = this.querySelector('[role="dialog"]');
      if (dialog) {
        trapFocus(this, dialog);
      }
      window.pauseAllMedia();
    }
    hide() {
      document.body.classList.remove("overflow-hidden");
      document.body.dispatchEvent(new CustomEvent("modalClosed"));
      this.removeAttribute("open");
      removeTrapFocus(this.openedBy ?? null);
      window.pauseAllMedia();
    }
  }
  // utils/components/modal-opener.ts
  class ModalOpener extends HTMLElement {
    constructor() {
      super();
      const button = this.querySelector("button");
      if (!button) return;
      button.addEventListener("click", () => {
        const modalSelector = this.getAttribute("data-modal");
        if (!modalSelector) return;
        const modal = document.querySelector(modalSelector);
        if (modal) modal.show(button);
      });
    }
  }
  // utils/constants.ts
  var ON_CHANGE_DEBOUNCE_TIMER = 300;
  var PUB_SUB_EVENTS = {
    cartUpdate: "cart-update",
    quantityUpdate: "quantity-update",
    optionValueSelectionChange: "option-value-selection-change",
    variantChange: "variant-change",
    cartError: "cart-error",
  };

  // utils/pubsub.ts
  var subscribers = {};
  var subscribe = (eventName, callback) => {
    if (subscribers[eventName] === undefined) {
      subscribers[eventName] = [];
    }
    subscribers[eventName] = [...subscribers[eventName], callback];
    return function unsubscribe() {
      subscribers[eventName] = subscribers[eventName].filter((cb) => {
        return cb !== callback;
      });
    };
  };
  var publish = (eventName, data) => {
    if (subscribers[eventName]) {
      const promises = subscribers[eventName].map((callback) => callback(data));
      return Promise.all(promises);
    }
    return Promise.resolve([]);
  };

  // utils/components/quantity-input.ts
  class QuantityInput extends HTMLElement {
    input;
    changeEvent;
    quantityUpdateUnsubscriber;
    constructor() {
      super();
      const input = this.querySelector("input");
      if (!input) {
        throw new Error("QuantityInput requires an input element");
      }
      this.input = input;
      this.changeEvent = new Event("change", { bubbles: true });
      this.input.addEventListener("change", this.onInputChange.bind(this));
      this.querySelectorAll("button").forEach((button) =>
        button.addEventListener("click", this.onButtonClick.bind(this)),
      );
    }
    connectedCallback() {
      this.validateQtyRules();
      this.quantityUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.quantityUpdate,
        this.validateQtyRules.bind(this),
      );
    }
    disconnectedCallback() {
      if (this.quantityUpdateUnsubscriber) {
        this.quantityUpdateUnsubscriber();
      }
    }
    onInputChange(_event) {
      this.validateQtyRules();
    }
    onButtonClick(event) {
      event.preventDefault();
      const buttonEvent = event;
      const previousValue = this.input.value;
      if (buttonEvent.target.name === "plus") {
        const minValue = this.input.dataset.min;
        if (
          minValue &&
          this.input.step &&
          parseInt(minValue) > parseInt(this.input.step) &&
          this.input.value === "0"
        ) {
          this.input.value = minValue;
        } else {
          this.input.stepUp();
        }
      } else {
        this.input.stepDown();
      }
      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
      if (
        this.input.dataset.min === previousValue &&
        buttonEvent.target.name === "minus"
      ) {
        this.input.value = this.input.min;
      }
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const buttonMinus = this.querySelector(
          ".quantity__button[name='minus']",
        );
        if (buttonMinus) {
          buttonMinus.classList.toggle(
            "disabled",
            value <= parseInt(this.input.min),
          );
        }
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        const buttonPlus = this.querySelector(".quantity__button[name='plus']");
        if (buttonPlus) {
          buttonPlus.classList.toggle("disabled", value >= max);
        }
      }
    }
  }
  // utils/components/slider-component.ts
  class SliderComponent extends HTMLElement {
    slider;
    sliderItems;
    enableSliderLooping;
    currentPageElement;
    pageTotalElement;
    prevButton;
    nextButton;
    sliderItemsToShow = [];
    sliderItemOffset = 0;
    slidesPerPage = 0;
    totalPages = 0;
    currentPage = 0;
    slideScrollPosition = 0;
    constructor() {
      super();
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.enableSliderLooping = false;
      this.currentPageElement = this.querySelector(".slider-counter--current");
      this.pageTotalElement = this.querySelector(".slider-counter--total");
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      if (!this.slider || !this.nextButton) return;
      this.initPages();
      const resizeObserver = new ResizeObserver(() => this.initPages());
      resizeObserver.observe(this.slider);
      this.slider.addEventListener("scroll", this.update.bind(this));
      this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
      this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
    }
    initPages() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(
        (element) => element.clientWidth > 0,
      );
      if (this.sliderItemsToShow.length < 2) return;
      const firstItem = this.sliderItemsToShow[0];
      const secondItem = this.sliderItemsToShow[1];
      this.sliderItemOffset = secondItem.offsetLeft - firstItem.offsetLeft;
      this.slidesPerPage = Math.floor(
        ((this.slider?.clientWidth ?? 0) - firstItem.offsetLeft) /
          this.sliderItemOffset,
      );
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    }
    resetPages() {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.initPages();
    }
    update() {
      if (!this.slider || !this.nextButton) return;
      const previousPage = this.currentPage;
      this.currentPage =
        Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = String(this.currentPage);
        this.pageTotalElement.textContent = String(this.totalPages);
      }
      if (this.currentPage !== previousPage) {
        this.dispatchEvent(
          new CustomEvent("slideChanged", {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow[this.currentPage - 1],
            },
          }),
        );
      }
      if (this.enableSliderLooping) return;
      if (
        this.isSlideVisible(this.sliderItemsToShow[0], 0) &&
        this.slider.scrollLeft === 0
      ) {
        this.prevButton?.setAttribute("disabled", "disabled");
      } else {
        this.prevButton?.removeAttribute("disabled");
      }
      if (
        this.isSlideVisible(
          this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
          0,
        )
      ) {
        this.nextButton?.setAttribute("disabled", "disabled");
      } else {
        this.nextButton?.removeAttribute("disabled");
      }
    }
    isSlideVisible(element, offset = 0) {
      if (!this.slider) return false;
      const htmlElement = element;
      const lastVisibleSlide =
        this.slider.clientWidth + this.slider.scrollLeft - offset;
      return (
        htmlElement.offsetLeft + htmlElement.clientWidth <= lastVisibleSlide &&
        htmlElement.offsetLeft >= this.slider.scrollLeft
      );
    }
    onButtonClick(event) {
      event.preventDefault();
      const target = event.currentTarget;
      const step = Number(target.dataset.step) || 1;
      this.slideScrollPosition =
        target.name === "next"
          ? (this.slider?.scrollLeft ?? 0) + step * this.sliderItemOffset
          : (this.slider?.scrollLeft ?? 0) - step * this.sliderItemOffset;
      this.setSlidePosition(this.slideScrollPosition);
    }
    setSlidePosition(position) {
      this.slider?.scrollTo({
        left: position,
      });
    }
  }
  // utils/components/slideshow-component.ts
  class SlideshowComponent extends SliderComponent {
    sliderControlWrapper;
    sliderFirstItemNode;
    announcementBarSlider = null;
    announcerBarAnimationDelay = 0;
    sliderControlLinksArray;
    announcementBarArrowButtonWasClicked;
    reducedMotion;
    autoplaySpeed;
    sliderAutoplayButton;
    autoplayButtonIsSetToPlay;
    autoplay;
    setPositionTimeout;
    sliderControlButtons;
    constructor() {
      super();
      this.sliderControlWrapper = this.querySelector(".slider-buttons");
      this.enableSliderLooping = true;
      this.announcementBarArrowButtonWasClicked = false;
      this.sliderControlLinksArray = [];
      if (!this.sliderControlWrapper) return;
      this.sliderFirstItemNode =
        this.slider?.querySelector(".slideshow__slide");
      if (this.sliderItemsToShow.length > 0) this.currentPage = 1;
      this.announcementBarSlider = this.querySelector(
        ".announcement-bar-slider",
      );
      this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;
      this.sliderControlLinksArray = Array.from(
        this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
      );
      this.sliderControlLinksArray.forEach((link) =>
        link.addEventListener("click", this.linkToSlide.bind(this)),
      );
      this.slider?.addEventListener(
        "scroll",
        this.setSlideVisibility.bind(this),
      );
      this.setSlideVisibility();
      if (this.announcementBarSlider) {
        this.announcementBarArrowButtonWasClicked = false;
        this.reducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        );
        this.reducedMotion.addEventListener("change", () => {
          if (this.slider?.getAttribute("data-autoplay") === "true")
            this.setAutoPlay();
        });
        [this.prevButton, this.nextButton].forEach((button) => {
          if (button) {
            button.addEventListener(
              "click",
              () => {
                this.announcementBarArrowButtonWasClicked = true;
              },
              { once: true },
            );
          }
        });
      }
      if (this.slider?.getAttribute("data-autoplay") === "true")
        this.setAutoPlay();
    }
    setAutoPlay() {
      const speed = this.slider?.dataset.speed;
      this.autoplaySpeed = Number(speed) * 1000;
      this.addEventListener("mouseover", this.focusInHandling.bind(this));
      this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
      this.addEventListener("focusin", this.focusInHandling.bind(this));
      this.addEventListener("focusout", this.focusOutHandling.bind(this));
      const autoplayButton = this.querySelector(".slideshow__autoplay");
      if (autoplayButton) {
        this.sliderAutoplayButton = autoplayButton;
        this.sliderAutoplayButton.addEventListener(
          "click",
          this.autoPlayToggle.bind(this),
        );
        this.autoplayButtonIsSetToPlay = true;
        this.play();
      } else {
        this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
          ? this.pause()
          : this.play();
      }
    }
    onButtonClick(event) {
      super.onButtonClick(event);
      const isFirstSlide = this.currentPage === 1;
      const isLastSlide = this.currentPage === this.sliderItemsToShow.length;
      if (!isFirstSlide && !isLastSlide) {
        const target2 = event.currentTarget;
        this.applyAnimationToAnnouncementBar(target2.name);
        return;
      }
      if (isFirstSlide && event.currentTarget.name === "previous") {
        this.slideScrollPosition =
          (this.slider?.scrollLeft ?? 0) +
          this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
      } else if (isLastSlide && event.currentTarget.name === "next") {
        this.slideScrollPosition = 0;
      }
      this.setSlidePosition(this.slideScrollPosition);
      const target = event.currentTarget;
      this.applyAnimationToAnnouncementBar(target.name);
    }
    setSlidePosition(position) {
      if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
      this.setPositionTimeout = window.setTimeout(() => {
        this.slider?.scrollTo({
          left: position,
        });
      }, this.announcerBarAnimationDelay);
    }
    update() {
      super.update();
      this.sliderControlButtons = Array.from(
        this.querySelectorAll(".slider-counter__link"),
      );
      this.prevButton?.removeAttribute("disabled");
      if (!this.sliderControlButtons.length) return;
      this.sliderControlButtons.forEach((link) => {
        link.classList.remove("slider-counter__link--active");
        link.removeAttribute("aria-current");
      });
      this.sliderControlButtons[this.currentPage - 1]?.classList.add(
        "slider-counter__link--active",
      );
      this.sliderControlButtons[this.currentPage - 1]?.setAttribute(
        "aria-current",
        "true",
      );
    }
    autoPlayToggle() {
      this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
      this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
      this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    }
    focusOutHandling(event) {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton =
          event.target === this.sliderAutoplayButton ||
          this.sliderAutoplayButton.contains(event.target);
        if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
        this.play();
      } else if (
        !this.reducedMotion.matches &&
        !this.announcementBarArrowButtonWasClicked
      ) {
        this.play();
      }
    }
    focusInHandling(event) {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton =
          event.target === this.sliderAutoplayButton ||
          this.sliderAutoplayButton.contains(event.target);
        if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
          this.play();
        } else if (this.autoplayButtonIsSetToPlay) {
          this.pause();
        }
      } else if (this.announcementBarSlider?.contains(event.target)) {
        this.pause();
      }
    }
    play() {
      this.slider?.setAttribute("aria-live", "off");
      clearInterval(this.autoplay);
      this.autoplay = window.setInterval(
        this.autoRotateSlides.bind(this),
        this.autoplaySpeed,
      );
    }
    pause() {
      this.slider?.setAttribute("aria-live", "polite");
      clearInterval(this.autoplay);
    }
    togglePlayButtonState(pauseAutoplay) {
      if (pauseAutoplay) {
        this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
        this.sliderAutoplayButton.setAttribute(
          "aria-label",
          window.accessibilityStrings.playSlideshow,
        );
      } else {
        this.sliderAutoplayButton.classList.remove(
          "slideshow__autoplay--paused",
        );
        this.sliderAutoplayButton.setAttribute(
          "aria-label",
          window.accessibilityStrings.pauseSlideshow,
        );
      }
    }
    autoRotateSlides() {
      const slideScrollPosition =
        this.currentPage === this.sliderItems.length
          ? 0
          : (this.slider?.scrollLeft ?? 0) + this.sliderItemOffset;
      this.setSlidePosition(slideScrollPosition);
      this.applyAnimationToAnnouncementBar();
    }
    setSlideVisibility(_event) {
      this.sliderItemsToShow.forEach((item, index) => {
        const linkElements = item.querySelectorAll("a");
        if (index === this.currentPage - 1) {
          if (linkElements.length) {
            linkElements.forEach((button) => {
              button.removeAttribute("tabindex");
            });
          }
          item.setAttribute("aria-hidden", "false");
          item.removeAttribute("tabindex");
        } else {
          if (linkElements.length) {
            linkElements.forEach((button) => {
              button.setAttribute("tabindex", "-1");
            });
          }
          item.setAttribute("aria-hidden", "true");
          item.setAttribute("tabindex", "-1");
        }
      });
    }
    applyAnimationToAnnouncementBar(button = "next") {
      if (!this.announcementBarSlider) return;
      const itemsCount = this.sliderItems.length;
      const increment = button === "next" ? 1 : -1;
      const currentIndex = this.currentPage - 1;
      let nextIndex = (currentIndex + increment) % itemsCount;
      nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;
      const nextSlide = this.sliderItems[nextIndex];
      const currentSlide = this.sliderItems[currentIndex];
      const animationClassIn = "announcement-bar-slider--fade-in";
      const animationClassOut = "announcement-bar-slider--fade-out";
      const isFirstSlide = currentIndex === 0;
      const isLastSlide = currentIndex === itemsCount - 1;
      const shouldMoveNext =
        (button === "next" && !isLastSlide) ||
        (button === "previous" && isFirstSlide);
      const direction = shouldMoveNext ? "next" : "previous";
      currentSlide.classList.add(`${animationClassOut}-${direction}`);
      nextSlide.classList.add(`${animationClassIn}-${direction}`);
      setTimeout(() => {
        currentSlide.classList.remove(`${animationClassOut}-${direction}`);
        nextSlide.classList.remove(`${animationClassIn}-${direction}`);
      }, this.announcerBarAnimationDelay * 2);
    }
    linkToSlide(event) {
      event.preventDefault();
      const target = event.currentTarget;
      const slideScrollPosition =
        (this.slider?.scrollLeft ?? 0) +
        this.sliderFirstItemNode.clientWidth *
          (this.sliderControlLinksArray.indexOf(target) + 1 - this.currentPage);
      this.slider?.scrollTo({
        left: slideScrollPosition,
      });
    }
  }
  // assets/scripts/global.ts
  window.publish = publish;
  window.subscribe = subscribe;
  window.PUB_SUB_EVENTS = PUB_SUB_EVENTS;
  window.ON_CHANGE_DEBOUNCE_TIMER = ON_CHANGE_DEBOUNCE_TIMER;
  window.pauseAllMedia = () => {
    document.querySelectorAll("video, iframe").forEach((media) => {
      if (media.tagName === "VIDEO") {
        media.pause();
      } else if (media.tagName === "IFRAME") {
        const iframe = media;
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*",
        );
      }
    });
    document.querySelectorAll("model-viewer")?.forEach((model) => {
      if (typeof model.pause === "function") {
        model.pause();
      }
    });
  };
  var focusVisiblePolyfill = () => {
    const navKeys = [
      "ARROWUP",
      "ARROWDOWN",
      "ARROWLEFT",
      "ARROWRIGHT",
      "TAB",
      "ENTER",
      "SPACE",
      "ESCAPE",
      "HOME",
      "END",
      "PAGEUP",
      "PAGEDOWN",
    ];
    let currentFocusedElement = null;
    let mouseClick = null;
    window.addEventListener("keydown", (event) => {
      if (navKeys.includes(event.code.toUpperCase())) {
        mouseClick = false;
      }
    });
    window.addEventListener("mousedown", (_event) => {
      mouseClick = true;
    });
    window.addEventListener(
      "focus",
      () => {
        if (currentFocusedElement)
          currentFocusedElement.classList.remove("focused");
        if (mouseClick) return;
        currentFocusedElement = document.activeElement;
        currentFocusedElement?.classList.add("focused");
      },
      true,
    );
  };
  var onKeyUpEscape = (event) => {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    const target = event.target;
    const openDetailsElement = target.closest("details[open]");
    if (!openDetailsElement) return;
    const summaryElement = openDetailsElement.querySelector("summary");
    if (!summaryElement) return;
    openDetailsElement.removeAttribute("open");
    summaryElement.setAttribute("aria-expanded", "false");
    summaryElement.focus();
  };
  document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
    summary.setAttribute("role", "button");
    const parentNode = summary.parentNode;
    summary.setAttribute(
      "aria-expanded",
      String(parentNode?.hasAttribute("open") ?? false),
    );
    const nextElement = summary.nextElementSibling;
    if (nextElement?.getAttribute("id")) {
      summary.setAttribute("aria-controls", nextElement.id);
    }
    summary.addEventListener("click", (event) => {
      const currentTarget = event.currentTarget;
      const detailsElement = currentTarget.closest("details");
      currentTarget.setAttribute(
        "aria-expanded",
        String(!detailsElement?.hasAttribute("open")),
      );
    });
    if (summary.closest("header-drawer, menu-drawer")) return;
    summary.parentElement?.addEventListener("keyup", onKeyUpEscape);
  });
  try {
    document.querySelector(":focus-visible");
  } catch {
    focusVisiblePolyfill();
  }
  if (typeof window.Shopify === "undefined") {
    window.Shopify = {};
  }
  window.Shopify.bind = (fn, scope) => {
    return (...args) => {
      return fn.apply(scope, args);
    };
  };
  window.Shopify.setSelectorByValue = (selector, value) => {
    for (let i = 0, count = selector.options.length; i < count; i++) {
      const option = selector.options[i];
      if (value === option.value || value === option.innerHTML) {
        selector.selectedIndex = i;
        return i;
      }
    }
    return;
  };
  window.Shopify.addListener = (target, eventName, callback) => {
    target.addEventListener(eventName, callback, false);
  };
  window.Shopify.postLink = (path, options) => {
    options = options || {};
    const method = options.method || "post";
    const params = options.parameters || {};
    const form = document.createElement("form");
    form.setAttribute("method", method);
    form.setAttribute("action", path);
    for (const key in params) {
      const hiddenField = document.createElement("input");
      hiddenField.setAttribute("type", "hidden");
      hiddenField.setAttribute("name", key);
      hiddenField.setAttribute("value", params[key]);
      form.appendChild(hiddenField);
    }
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };
  window.Shopify.CountryProvinceSelector = function (
    country_domid,
    province_domid,
    options,
  ) {
    this.countryEl = document.getElementById(country_domid);
    this.provinceEl = document.getElementById(province_domid);
    this.provinceContainer = document.getElementById(
      options.hideElement || province_domid,
    );
    if (this.countryEl) {
      window.Shopify.addListener(
        this.countryEl,
        "change",
        window.Shopify.bind(this.countryHandler, this),
      );
    }
    this.initCountry();
    this.initProvince();
  };
  window.Shopify.CountryProvinceSelector.prototype = {
    initCountry() {
      if (!this.countryEl) return;
      const value = this.countryEl.getAttribute("data-default");
      if (value) {
        window.Shopify.setSelectorByValue(this.countryEl, value);
      }
      this.countryHandler();
    },
    initProvince() {
      if (!this.provinceEl) return;
      const value = this.provinceEl.getAttribute("data-default");
      if (value && this.provinceEl.options.length > 0) {
        window.Shopify.setSelectorByValue(this.provinceEl, value);
      }
    },
    countryHandler(_e) {
      if (!this.countryEl || !this.provinceEl || !this.provinceContainer)
        return;
      const opt = this.countryEl.options[this.countryEl.selectedIndex];
      const raw = opt?.getAttribute("data-provinces");
      if (!raw) return;
      const provinces = JSON.parse(raw);
      this.clearOptions(this.provinceEl);
      if (provinces && provinces.length === 0) {
        this.provinceContainer.style.display = "none";
      } else {
        for (let i = 0; i < provinces.length; i++) {
          const option = document.createElement("option");
          option.value = provinces[i][0];
          option.innerHTML = provinces[i][1];
          this.provinceEl.appendChild(option);
        }
        this.provinceContainer.style.display = "";
      }
    },
    clearOptions(selector) {
      while (selector.firstChild) {
        selector.removeChild(selector.firstChild);
      }
    },
    setOptions(selector, values) {
      for (let i = 0; i < values.length; i++) {
        const opt = document.createElement("option");
        opt.value = values[i];
        opt.innerHTML = values[i];
        selector.appendChild(opt);
      }
    },
  };
  if (!customElements.get("modal-dialog")) {
    customElements.define("modal-dialog", ModalDialog);
  }
  if (!customElements.get("modal-opener")) {
    customElements.define("modal-opener", ModalOpener);
  }
  if (!customElements.get("deferred-media")) {
    customElements.define("deferred-media", DeferredMedia);
  }
  if (!customElements.get("slider-component")) {
    customElements.define("slider-component", SliderComponent);
  }
  if (!customElements.get("slideshow-component")) {
    customElements.define("slideshow-component", SlideshowComponent);
  }
  if (!customElements.get("quantity-input")) {
    customElements.define("quantity-input", QuantityInput);
  }
})();
